import {
  GetPersonByIdV2UseCaseInterface,
  UpdatePersonUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useAuth } from 'presentation/app/hooks/useAuth';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { personFormInitialValues } from '../utils/validation/person-form-initial-values';
import { personFormSchema } from '../utils/validation/person-form-schema';
import { personFormTransformValuesToSubmit } from '../utils/validation/person-form-transform-values-to-submit';

interface IPersonFormHookProps {
  person?: GetPersonByIdV2UseCaseInterface.Result;
  requestGetPersonById?: (
    params: GetPersonByIdV2UseCaseInterface.Params,
  ) => void;
}

export const usePersonForm = ({
  person,
  requestGetPersonById,
}: IPersonFormHookProps) => {
  const [loadingUpdatePerson, setLoadingUpdatePerson] =
    useState<boolean>(false);

  const { currentUser } = useAuth();
  const { personService } = useService();
  const { id } = useParams<{
    id: string;
  }>();

  const requestUpdatePerson = useCallback(
    async (params: UpdatePersonUseCaseInterface.Params) => {
      setLoadingUpdatePerson(true);

      try {
        const res = await personService.updatePerson(params);
        notifySuccess(res.message);
        requestGetPersonById({ id });
      } catch (error) {
        if (error instanceof Error) {
          notifyError(error.message);
        }
      } finally {
        setLoadingUpdatePerson(false);
      }
    },
    [],
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: personFormInitialValues(person),
    validationSchema: personFormSchema(),
    onSubmit: (values) => {
      const valuesToSubmit = personFormTransformValuesToSubmit(values);
      requestUpdatePerson({
        id,
        updaterId: currentUser.id,
        updatePerson: valuesToSubmit,
      });
    },
  });

  return {
    person,
    validation,
    requestUpdatePerson,
    loadingUpdatePerson,
  };
};
